import React, { useState } from "react";
import {
  Alert,
  Modal,
  Radio,
  Input,
  Button,
  DatePicker,
  Select,
  Descriptions,
  Typography,
  Col,
  Row,
} from "antd";
import {
  useSource,
  useTranslation,
  SourcePicker,
  useOpenDashApp,
} from "@opendash/core";
import { Parse } from "@openinc/parse-hooks";
import dayjs, { Dayjs } from "dayjs";
import { set } from "mobx";

const { Text } = Typography;

const { Option } = Select;

interface CreateServiceModalProps {
  edit: boolean;
  editButtonClicked: boolean;
  changes: boolean;
  visible: boolean;
  onCancel: () => void;
  spsid: string;
}

const CreateServiceModal: React.FC<CreateServiceModalProps> = ({
  edit,
  editButtonClicked,
  changes,
  visible,
  onCancel,
  spsid,
}) => {
  const [isActive, setIsActive] = useState<boolean>(edit);
  const [source] = useSource();
  const t = useTranslation();
  const app = useOpenDashApp();
  const [languages, setLanguages] = React.useState<
    { code: string; label: string }[]
  >([]);
  const [editBoolean, setEditBoolean] = useState<boolean>(false);
  const [spsidValue, setSPSIDValue] = useState<string>(spsid);
  const [sourceSelected, setSourceSelected] = useState<any>(source);
  const [currentOPHours, setCurrentOPHours] = useState<number>(0);
  const [machineName, setMachineName] = useState<string>("");
  const [ophoursStatus, setOphoursStatus] = useState<number>(2);
  const [hourStatus, setHourStatus] = useState<number>(2);
  const [mail, setMail] = useState<string>("");
  const [validate, setValidate] = useState<boolean>(true);
  const [validateText, setValidateText] = useState<string>("");
  const [ophoursValueStart, setOphoursValueStart] = useState<string>("");
  const [ophoursValue, setOphoursValue] = useState<string>("");
  const [myStartDate, setMyStartDate] = useState<Dayjs | null>(null);
  const [hoursValue, setHoursValue] = useState<string>("");
  const [itemDate, setItemDate] = useState<{ id: number; name: string }[]>([
    //{ id: 99, name: "Täglich (Test)" },
    { id: 1, name: "Monatlich" },
    { id: 2, name: "Vierteljährlich" },
    { id: 3, name: "Halbjährlich" },
    { id: 4, name: "Jährlich" },
    { id: 5, name: "2-Jährig" },
  ]);

  const setSPSID = async (spsid: string) => {
    setSPSIDValue(spsid);
    const sourcePointer = new Parse.Query("OD3_Source").limit(1000000);
    sourcePointer.equalTo("tag", spsid);
    const results = await sourcePointer.first();
    setSourceSelected(results);
  };

  const getStoredLanguage = () => {
    const storedLang = window.localStorage.getItem("opendash:language");
    return storedLang ? JSON.parse(storedLang) : "de";
  };
  const [selectedLanguage, setSelectedLanguage] = React.useState<{
    code: string;
    label: string;
  } | null>(null);

  React.useEffect(() => {
    setLanguages(
      app.ui.languages.map((item) => ({
        code: item.key,
        label: item.label,
      }))
    );
  }, []);

  React.useEffect(() => {
    if (!languages) return;
    setSelectedLanguage(
      languages.find((lang) => lang.code === getStoredLanguage()) ||
        languages[0]
    );
  }, [languages]);

  const loadDate = async () => {
    const ServiceEntry = Parse.Object.extend("HG_Service");
    const serviceQuery = new Parse.Query(ServiceEntry).limit(1000000);

    let sourceTag = "";
    if (editButtonClicked) {
      if (sourceSelected.tag !== spsid) {
        sourceTag = spsid;
        setSPSID(spsid);
      }
    } else {
      sourceTag = spsidValue || sourceSelected?.tag;
    }
    if (sourceTag) {
      const sourceQuery = new Parse.Query("OD3_Source").limit(1000000);
      sourceQuery.equalTo("tag", sourceTag);

      sourceQuery
        .first()
        .then((sourceResult) => {
          if (sourceResult) {
            serviceQuery.include("source");
            serviceQuery.equalTo("source", sourceResult);

            serviceQuery
              .first()
              .then((serviceResult) => {
                if (serviceResult) {
                  // Eintrag gefunden, Edit-Modus aktivieren
                  setEditBoolean(true);

                  setCurrentOPHours(serviceResult.get("op_hours"));
                  setMachineName(
                    serviceResult.get("source").get("name") +
                      " (" +
                      (serviceResult.get("source").get("machineNumber") ??
                        serviceResult.get("source").get("tag")) +
                      ")"
                  );

                  // Setzen der Zustände aus dem vorhandenen Eintrag
                  const serviceBody = serviceResult.get("service_body");
                  if (serviceBody && serviceBody.length > 0) {
                    // Betriebsstunden-Service
                    if (serviceBody[0]) {
                      setMail(serviceBody[0].mail);
                      setOphoursStatus(serviceBody[0].active);
                      setOphoursValueStart(serviceBody[0].start);
                      setOphoursValue(serviceBody[0].value);
                    }

                    // Zeit-Service
                    if (serviceBody[1]) {
                      setHourStatus(serviceBody[1].active);
                      setMyStartDate(
                        serviceBody[1].start
                          ? dayjs(serviceBody[1].start)
                          : null
                      );
                      setHoursValue(serviceBody[1].value);
                    }
                  } else {
                    setMail("");
                    setOphoursStatus(2);
                    setOphoursValueStart("");
                    setOphoursValue("");
                    setHourStatus(2);
                    setMyStartDate(null);
                    setHoursValue("");
                  }

                  // Überprüfen, ob ein aktiver Alarm vorhanden ist
                  const lastServices = serviceResult.get("last_services");
                  if (Array.isArray(lastServices) && lastServices.length > 0) {
                    const lastService = lastServices[lastServices.length - 1];
                    if (lastService && lastService.active === true) {
                      setIsActive(true);
                    } else {
                      setIsActive(false);
                    }
                  } else {
                    setIsActive(false);
                  }
                } else {
                  // Kein Eintrag gefunden, Edit-Modus deaktivieren
                  setEditBoolean(false);
                  setCurrentOPHours(0);
                  setMachineName("");
                  setIsActive(false);
                  // Weitere Zustände zurücksetzen
                }
              })
              .catch((error) => {
                setEditBoolean(false);
                setIsActive(false);
              });
          } else {
            setEditBoolean(false);
            setIsActive(false);
          }
        })
        .catch((error) => {
          setEditBoolean(false);
          setIsActive(false);
        });
    } else {
      setEditBoolean(false);
      setIsActive(false);
    }
  };

  React.useEffect(() => {
    loadDate();
  }, []);

  React.useEffect(() => {
    loadDate();
  }, [visible, changes, sourceSelected, spsidValue]);

  const sendEntry = async () => {
    // Validate
    let error = 1;
    let validatePuffer = true;

    const operatingHoursValue = parseInt(ophoursValue);
    const operatingHoursValueStart = parseInt(ophoursValueStart);

    if (ophoursStatus === 1 && operatingHoursValue && operatingHoursValue > 0) {
      if (operatingHoursValueStart < currentOPHours) {
        validatePuffer = false;
        setValidate(false);
        setValidateText(
          t("greis:serviceview.new_modal.validate_text_operating_hours_start")
        );
        error = 2;
      }
    } else if (ophoursStatus !== 2) {
      validatePuffer = false;
      setValidate(false);
      setValidateText(
        t("greis:serviceview.new_modal.validate_text_operating_hours_error")
      );
    }
    if (hourStatus === 1 && hoursValue && parseInt(hoursValue) > 0) {
      const pick = dayjs(myStartDate);
      const now = dayjs().endOf("day");

      if (pick.valueOf() <= now.valueOf() && hoursValue != "99") {
        validatePuffer = false;
        setValidate(false);
        setValidateText(
          t("greis:serviceview.new_modal.validate_text_time_start")
        );
        error = 2;
      }
    } else if (hourStatus !== 2) {
      validatePuffer = false;
      setValidate(false);
      setValidateText(
        t("greis:serviceview.new_modal.validate_text_time_error")
      );
    }

    const re =
      /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
    if (!mail || mail == "" || (mail && !re.test(mail))) {
      validatePuffer = false;
      setValidate(false);
      setValidateText(
        t("greis:serviceview.new_modal.validate_text_mail_error")
      );
    }

    if (validatePuffer) {
      setValidate(true);
      try {
        const serviceArray: any[] = []; // Typ für serviceArray
        const serviceOPHObject: any = {}; // Typ für serviceOPHObject
        const serviceHObject: any = {}; // Typ für serviceHObject

        serviceOPHObject.type = "op";
        serviceOPHObject.active = ophoursStatus;
        serviceOPHObject.start = operatingHoursValueStart || 0; // Standardwert
        serviceOPHObject.value =
          ophoursStatus === 2 ? "-1" : operatingHoursValue.toString();
        serviceOPHObject.mail = mail;

        serviceHObject.type = "hour";
        serviceHObject.active = hourStatus;
        serviceHObject.start = myStartDate
          ? dayjs(myStartDate).startOf("day").valueOf()
          : 0;

        serviceHObject.value = hourStatus === 2 ? "-1" : hoursValue;
        serviceHObject.mail = mail;

        serviceArray.push(serviceOPHObject);
        serviceArray.push(serviceHObject);

        const ServiceEntry = Parse.Object.extend("HG_Service");
        const query = new Parse.Query(ServiceEntry).limit(1000000);

        const sourcePointer = new Parse.Object("OD3_Source");
        sourcePointer.id = sourceSelected.id;

        query.include("source");
        query.equalTo("source", sourcePointer);
        const results = await query.find();

        if (results.length === 0) {
          const newService = new ServiceEntry();
          newService.set("source", sourcePointer);
          newService.set("service_body", serviceArray);
          newService.set("op_hours", 0);
          newService.set("language", selectedLanguage?.code);
          newService.set("last_op_hours_fired", currentOPHours ?? 0);
          newService.set("last_ohours", currentOPHours ?? 0);
          await newService.save();
          deleteEntry();
          onCancel();
        } else {
          if (editBoolean) {
            if (serviceHObject.start > 0) {
              if (dayjs().startOf("day").valueOf() > serviceHObject.start) {
                let lastFiredFake = serviceHObject.start;
                let lastFiredFakePuffer = lastFiredFake;
                results[0].set("last_hours_fired", lastFiredFakePuffer);
              } else {
                results[0].set("last_hours_fired", 0);
              }
            }
          }
          let language = window.localStorage.getItem("opendash:language")
            ? JSON.parse(
                window.localStorage.getItem("opendash:language") || '"de"'
              )
            : "de";
          results[0].set("language", selectedLanguage?.code + "");
          results[0].set("service_body", serviceArray);
          results[0].set("last_op_hours_fired", currentOPHours ?? 0);
          results[0].set("last_ohours", currentOPHours ?? 0);
          await results[0].save();
          deleteEntry();
          onCancel();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const languageSelect = languages && languages.length > 1 && (
    <Select
      style={{ width: "100%" }}
      value={selectedLanguage?.code || languages[0]?.code} // Fallback auf die erste Sprache
      onChange={(value) => {
        setSelectedLanguage(
          languages.find((lang) => lang.code === value) || null
        );
      }}
    >
      {languages.length > 0 &&
        languages.map((lang) => (
          <Select.Option key={lang.code} value={lang.code}>
            {lang.label}
          </Select.Option>
        ))}
    </Select>
  );

  const deleteEntry = () => {
    setOphoursStatus(2);
    setOphoursValue("");
    setOphoursValueStart("");
    setHourStatus(2);
    setHoursValue("");
    setMail("");
    setMyStartDate(null);
    setValidate(true);
    setEditBoolean(false);
    setIsActive(false);
    setSPSIDValue("");
    setSourceSelected(source);
  };

  return (
    <Modal
      destroyOnClose={true}
      title={
        machineName != ""
          ? t("greis:serviceview.new_modal.title_with") + machineName
          : t("greis:serviceview.new_modal.title")
      }
      open={visible}
      onCancel={() => {
        deleteEntry();
        onCancel();
      }}
      footer={null}
      width={855}
      style={{ maxHeight: "90%" }}
    >
      {!validate && (
        <>
          <Alert message={validateText} type="error" />
          <br />
        </>
      )}
      {isActive && (
        <>
          <Alert
            message={t("greis:serviceview.new_modal.alarm_active")}
            type="error"
          />
          <br />
        </>
      )}
      <Descriptions bordered column={1} style={{ marginBottom: "20px" }}>
        {!editButtonClicked && (
          <Descriptions.Item
            label={t("greis:serviceview.new_modal.machine_select")}
          >
            <SourcePicker
              style={{ width: "100%" }}
              value={sourceSelected}
              onChange={(newSource) => {
                setSourceSelected(newSource);
                setSPSIDValue(newSource.tag);
              }}
            />
          </Descriptions.Item>
        )}
        {!isActive && (
          <>
            <Descriptions.Item
              label={t("greis:serviceview.new_modal.desc_op_title")}
            >
              <Radio.Group
                onChange={(e) => {
                  setValidate(true);
                  setOphoursStatus(e.target.value);
                }}
                value={ophoursStatus}
              >
                <Radio value={1}>
                  {t("greis:serviceview.new_modal.desc_activate")}
                </Radio>
                <Radio value={2}>
                  {t("greis:serviceview.new_modal.desc_deactivate")}
                </Radio>
              </Radio.Group>
            </Descriptions.Item>

            {ophoursStatus === 1 && (
              <>
                <Descriptions.Item
                  label={
                    t("greis:serviceview.new_modal.desc_op_start") +
                    " - " +
                    currentOPHours +
                    "):"
                  }
                >
                  <Input
                    placeholder={t(
                      "greis:serviceview.new_modal.desc_enter_value"
                    )}
                    type="number"
                    value={ophoursValueStart}
                    onChange={(e) => {
                      setValidate(true);
                      setOphoursValueStart(e.target.value);
                    }}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("greis:serviceview.new_modal.desc_op_interval")}
                >
                  <Input
                    placeholder={t(
                      "greis:serviceview.new_modal.desc_enter_value"
                    )}
                    type="number"
                    value={ophoursValue}
                    onChange={(e) => {
                      setValidate(true);
                      setOphoursValue(e.target.value);
                    }}
                  />
                </Descriptions.Item>
              </>
            )}

            <Descriptions.Item
              label={t("greis:serviceview.new_modal.desc_time_title")}
            >
              <Radio.Group
                onChange={(e) => {
                  setValidate(true);
                  setHourStatus(e.target.value);
                }}
                value={hourStatus}
              >
                <Radio value={1}>
                  {t("greis:serviceview.new_modal.desc_activate")}
                </Radio>
                <Radio value={2}>
                  {t("greis:serviceview.new_modal.desc_deactivate")}
                </Radio>
              </Radio.Group>
            </Descriptions.Item>

            {hourStatus === 1 && (
              <>
                <Descriptions.Item
                  label={t("greis:serviceview.new_modal.desc_time_start")}
                >
                  <DatePicker
                    format="DD.MM.YYYY"
                    value={myStartDate ? dayjs(myStartDate) : null}
                    onChange={(date) => {
                      setValidate(true);
                      setMyStartDate(date);
                    }}
                    style={{ width: "100%", padding: 0, margin: 0 }}
                  />
                </Descriptions.Item>
                <Descriptions.Item
                  label={t("greis:serviceview.new_modal.desc_time_interval")}
                >
                  <Select
                    value={hoursValue}
                    onChange={(value) => {
                      setValidate(true);
                      setHoursValue(value);
                    }}
                    style={{
                      width: "100%",
                      textAlign: "center",
                      textIndent: "25%",
                    }}
                  >
                    {itemDate.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                  </Select>
                </Descriptions.Item>
              </>
            )}

            <Descriptions.Item
              label={t("greis:serviceview.new_modal.desc_language")}
            >
              {languageSelect}
            </Descriptions.Item>

            <Descriptions.Item
              label={t("greis:serviceview.new_modal.desc_time_mail_receiver")}
            >
              <Input
                placeholder={t(
                  "greis:serviceview.new_modal.desc_time_mail_receiver_hint"
                )}
                value={mail}
                onChange={(e) => setMail(e.target.value)}
              />
            </Descriptions.Item>
          </>
        )}
      </Descriptions>
      {!isActive && (
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <Button type="primary" onClick={sendEntry} style={{ width: "80%" }}>
            <i className="fa fa-plus-square" aria-hidden="true"></i>{" "}
            {t("greis:serviceview.new_modal.button_save")}
          </Button>
          &nbsp;
          <Button
            onClick={() => {
              deleteEntry();
              onCancel();
            }}
            style={{ width: "80%" }}
          >
            <i className="fa fa-trash" aria-hidden="true"></i>{" "}
            {t("greis:serviceview.new_modal.button_cancel")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CreateServiceModal;
